import { z } from 'zod';

/* Contratos da fila de moderação de packs no backoffice (TASK-29-14, ADR-0363 §4).
   O admin-service expõe `/v1/admin/pack-submissions` (list) + approve/reject/
   request-changes (TASK-29-05). O DTO é a visão da UI — campos internos
   (`packFileR2Key`, `scanResults` cru) ficam fora. */

export const packSubmissionStatusSchema = z.enum([
  'submitted',
  'scanning',
  'reviewing',
  'approved',
  'rejected',
  'changes_requested',
  /* Cancelada pelo próprio publisher (ADR-0460). Espelha o enum do DB `pack_review_queue`
     — sem isto, submissões canceladas quebrariam o parse da lista/detalhe no admin-web. */
  'cancelled',
]);
export type PackSubmissionStatus = z.infer<typeof packSubmissionStatusSchema>;

export const packScanStatusSchema = z.enum(['pending', 'pass', 'fail', 'warn']);
export type PackScanStatus = z.infer<typeof packScanStatusSchema>;

export const listPackSubmissionsQuery = z.object({
  status: packSubmissionStatusSchema.optional(),
  /* Filtros server-side que particionam a fila INTEIRA (não só a página keyset carregada),
     ADR-0712: publisher exato (índice `pack_review_queue_publisher_idx`), resultado do scan e
     janela inferior por data de envio. `submittedAfter` coexiste com o `cursor`/`before` keyset. */
  publisherId: z.string().min(1).optional(),
  scanStatus: packScanStatusSchema.optional(),
  submittedAfter: z.string().datetime().optional(),
  cursor: z.string().min(1).optional(),
  limit: z.coerce.number().int().min(1).max(200).default(50),
});
export type ListPackSubmissionsQuery = z.infer<typeof listPackSubmissionsQuery>;

export const packSubmissionDto = z.object({
  id: z.string(),
  packId: z.string(),
  version: z.string(),
  publisherId: z.string(),
  status: packSubmissionStatusSchema,
  scanStatus: packScanStatusSchema.nullish(),
  reviewedByStaffId: z.string().nullish(),
  reviewNotes: z.string().nullish(),
  iterationCount: z.number().int(),
  fastpathApplied: z.boolean(),
  submittedAt: z.string(),
  reviewedAt: z.string().nullish(),
});
export type PackSubmission = z.infer<typeof packSubmissionDto>;

export const packSubmissionsPageDto = z.object({
  submissions: z.array(packSubmissionDto),
  nextCursor: z.string().nullish(),
});

/* Moderação homogênea em lote (ADR-0829). A justificativa é obrigatória mesmo nas transições
   individuais que não pedem motivo: ela pertence à decisão do lote e fica na trilha append-only.
   Cada ID ainda passa pela máquina de estados/CAS do admin-service; não há promessa de atomicidade
   entre submissões. */
export const packSubmissionActionSchema = z.enum([
  'start-review',
  'approve',
  'reject',
  'request-changes',
  'reopen',
]);
export type PackSubmissionAction = z.infer<typeof packSubmissionActionSchema>;

export const PACK_SUBMISSION_BULK_MAX_IDS = 100;
export const bulkPackSubmissionActionBody = z.object({
  ids: z
    .array(z.string().min(1))
    .min(1)
    .max(PACK_SUBMISSION_BULK_MAX_IDS)
    .refine((ids) => new Set(ids).size === ids.length, { message: 'duplicate_bulk_ids' }),
  action: packSubmissionActionSchema,
  reason: z.string().trim().min(1).max(2000),
});
export type BulkPackSubmissionActionBody = z.infer<typeof bulkPackSubmissionActionBody>;

export const bulkPackSubmissionActionResponseDto = z.object({
  results: z.array(
    z.object({
      id: z.string(),
      ok: z.boolean(),
      error: z.string().nullish(),
    }),
  ),
});
export type BulkPackSubmissionActionResponse = z.infer<typeof bulkPackSubmissionActionResponseDto>;

/* --- Detalhe da submissão (PUB-11 / ADR-0462) --------------------------------------------
   O `GET /v1/admin/pack-submissions/:id` monta este DTO rico para o backoffice decidir sem
   ocultar nada: findings estruturados + timeline + versões anteriores + publisher. */

/* Espelha `scanResultSchema` de `@planuze/pack-scanner` (fonte de verdade: ADR-0370). Cópia
   de contrato deliberada (não import) — `admin-contracts` é client-safe (browser); importar o
   pack-scanner puxaria engine/regras/Node deps e arriscaria o boundary (ADR-0462 §3). */
export const scanSeverityDto = z.enum(['error', 'warning', 'info']);
export type ScanSeverity = z.infer<typeof scanSeverityDto>;

export const scannerFindingDto = z.object({
  ruleId: z.string(),
  severity: scanSeverityDto,
  file: z.string(),
  line: z.number().int().nonnegative(),
  column: z.number().int().nonnegative(),
  snippet: z.string(),
  message: z.string(),
  suggestion: z.string().optional(),
  allowedByManifest: z.boolean().optional(),
});
export type ScannerFinding = z.infer<typeof scannerFindingDto>;

export const scanResultsDto = z.object({
  /* `pending` = scan não rodou → o endpoint devolve `scanResults: null`, nunca este objeto. */
  status: z.enum(['pass', 'fail', 'warn']),
  findings: z.array(scannerFindingDto),
  filesScanned: z.number().int().nonnegative(),
  durationMs: z.number().int().nonnegative(),
  truncated: z.boolean(),
  scannerVersion: z.string(),
  /* Presente no scanner atual; opcional preserva rows históricas. Bloqueia fastpath quando true. */
  dangerousSelfAttestation: z.boolean().optional(),
  /* Procedência do scan OIDC, já vinculada pelo registry ao fileHash. Findings continuam flat
     para o backoffice não degradar o relatório atestado para “scan não rodou”. */
  attestation: z
    .object({
      source: z.literal('github_oidc_attestation'),
      checksum: z.string().regex(/^[a-f0-9]{64}$/),
      identity: z.object({
        workflowRef: z.string(),
        workflowSha: z.string().regex(/^[a-f0-9]{40}$/),
        subject: z.string().optional(),
        repository: z.string().optional(),
        repositoryId: z.string().optional(),
        runId: z.string().optional(),
      }),
      attestedAt: z.string().datetime(),
    })
    .optional(),
});
export type ScanResults = z.infer<typeof scanResultsDto>;

/* Resumo das submissões ANTERIORES do mesmo pack (confronto de versões — ADR-0462 §4). */
export const priorVersionSummaryDto = z.object({
  version: z.string(),
  status: packSubmissionStatusSchema,
  scanStatus: packScanStatusSchema.nullish(),
  submittedAt: z.string(),
  reviewedAt: z.string().nullish(),
});
export type PriorVersionSummary = z.infer<typeof priorVersionSummaryDto>;

export const submissionPublisherDto = z.object({
  id: z.string(),
  displayName: z.string(),
  /* `suspended` incluído (ADR-0471) — sem ele o parse quebra p/ submissão de publisher suspenso. */
  status: z.enum(['pending', 'verified', 'suspended', 'banned']),
});
export type SubmissionPublisher = z.infer<typeof submissionPublisherDto>;

/* Diff estruturado de manifest entre 2 versões publicadas (ADR-0468/0473). Espelha o shape de
   `diffManifests` do registry; `minAppVersionChange` é OPCIONAL (`.optional()`, não nullable). */
export const versionDiffDto = z.object({
  from: z.string(),
  to: z.string(),
  capabilitiesAdded: z.array(z.string()),
  capabilitiesRemoved: z.array(z.string()),
  minAppVersionChange: z.object({ from: z.string(), to: z.string() }).optional(),
  channelsAdded: z.array(z.string()),
  channelsRemoved: z.array(z.string()),
});
export type VersionDiffDto = z.infer<typeof versionDiffDto>;

/* Espelha o shape REAL de `listSubmissionTransitions` (repo PUB-10 / ADR-0461):
   `fromStatus`/`toStatus`/`actorKind`/`scanStatus` são valores de audit históricos (podem
   conter `cancelled`/`scanning`) → `z.string()`, não o enum fechado. `at` = ISO string. */
export const submissionTransitionDto = z.object({
  fromStatus: z.string(),
  toStatus: z.string(),
  actorKind: z.string(),
  actorStaffId: z.string().nullish(),
  reason: z.string().nullish(),
  scanStatus: z.string().nullish(),
  at: z.string(),
});
export type SubmissionTransition = z.infer<typeof submissionTransitionDto>;

export const packSubmissionDetailDto = packSubmissionDto.extend({
  /* Metadados do binário/procedência já presentes na row do admin-service (ADR-0507/0515/0526),
     propagados ao detalhe sem custo (o `getSubmission` já os traz no `select()`). Todos NULLABLE:
     colunas nullable no DB (submissões pré-migração / sem escrow) — o parse do detalhe quebraria
     se fossem obrigatórios. `kind` = template/extension/module-pack; `fileBytes`/`fileHash` =
     tamanho+sha256 do .plnzpack; `publisherFingerprint` = `sha256:…` da pubkey; `ownerUserId` =
     conta dona (x-publisher-user); `scannedAt` = quando o scan rodou (ISO). */
  kind: z.string().nullish(),
  fileBytes: z.number().int().nonnegative().nullish(),
  fileHash: z.string().nullish(),
  publisherFingerprint: z.string().nullish(),
  ownerUserId: z.string().nullish(),
  scannedAt: z.string().nullish(),
  scanResults: scanResultsDto.nullish(),
  transitions: z.array(submissionTransitionDto),
  previousVersions: z.array(priorVersionSummaryDto),
  publisher: submissionPublisherDto.nullish(),
  /* Diff de campos do manifest (submissão vs versão anterior com snapshot, ADR-0475). `null` =
     sem versão anterior com snapshot ou snapshot ausente/malformado (fail-soft). Reusa o shape
     do `versionDiffDto` (ADR-0473). */
  manifestDiff: versionDiffDto.nullish(),
});
export type PackSubmissionDetail = z.infer<typeof packSubmissionDetailDto>;

/* Disparo manual do scan (ADR-0731). `enqueued` = re-enfileirado + drain disparado; `runner_unavailable`
   = scanner runner não configurado/indisponível (drain no-op); `no_scan_blob` = submissão sem blob
   escaneável (só revisão manual). */
export const rescanResponseDto = z.object({
  status: z.enum(['enqueued', 'runner_unavailable', 'no_scan_blob']),
});
export type RescanResponse = z.infer<typeof rescanResponseDto>;
